import React, {useState, useEffect} from 'react';
import useDecodeHtml from './useDecodeHtml';
import {
    InstagramEmbed,
    XEmbed,
    YouTubeEmbed,
    FacebookEmbed
} from 'react-social-media-embed';
import Slideshow from '../components/Slideshow/Slideshow';

const sanitizeContent = (content) => {
    // Funzione per rimuovere script, eventi o tag pericolosi
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;

    // Rimuove script e tag non sicuri
    const scripts = tempDiv.querySelectorAll('script');
    scripts.forEach(script => script.remove());

    // Rimuove attributi pericolosi come onload, onclick
    const elements = tempDiv.querySelectorAll('*');
    elements.forEach(el => {
        for (const attr of el.attributes) {
            if (attr.name.startsWith('on')) {
                el.removeAttribute(attr.name);
            }
        }
    });

    return tempDiv.innerHTML;
};

const normalizeUrl = (service, src) => {
    if (service === 'instagram') {
        const match = src.match(/https:\/\/www\.instagram\.com\/(reel|p|tv)\/([^/?#&]+)/);
        return match ? `https://www.instagram.com/${match[1]}/${match[2]}/` : src;
    }

    if (service === 'youtube') {
        const match = src.match(/(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
        return match ? `https://www.youtube.com/embed/${match[1]}` : src;
    }

    return src;
};

const useRenderBlocks = (blocks) => {
    console.log(blocks,'blocks');
    const decodeHtml = useDecodeHtml();
    const [lightboxImages, setLightboxImages] = useState([]);
    const [lightboxIndex, setLightboxIndex] = useState(null);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                setLightboxIndex(null);
            } else if (e.key === 'ArrowRight') {
                setLightboxIndex(prev => (prev !== null && prev < lightboxImages.length - 1 ? prev + 1 : prev));
            } else if (e.key === 'ArrowLeft') {
                setLightboxIndex(prev => (prev !== null && prev > 0 ? prev - 1 : prev));
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [lightboxImages.length]);

    const navButtonStyle = (side) => ({
        position: 'absolute',
        top: '50%',
        [side]: '1rem',
        transform: 'translateY(-50%)',
        fontSize: '2.5rem',
        color: '#fff',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        zIndex: 10000,
    });

    if (!Array.isArray(blocks) || blocks.length === 0) {
        return <div className="block-content">No content available</div>;
    }

    const renderBlocks = (blocks) => {
        console.log(blocks, 'blocks')
        return blocks.map((block, index) => {
            const normalizedSrc = normalizeUrl(block.service, block.src);
            switch (block.type) {
                case 'group':
                    return (
                        <div key={index} className="block-group">
                            {renderBlocks(block.blocks)}
                        </div>
                    );
                case 'paragraph':
                    return (
                        <p
                            key={index}
                            className="block-paragraph"
                            dangerouslySetInnerHTML={{ __html: sanitizeContent(block.content) }}
                        />
                    );
                case 'heading':
                    return React.createElement(
                        `h${block.level || 2}`,
                        { key: index, className: "block-heading" },
                        <span dangerouslySetInnerHTML={{ __html: sanitizeContent(decodeHtml(block.content)) }} />
                    );
                case 'image':
                    return (
                        <figure key={index} className="block-image">
                            <img src={block.src} alt={sanitizeContent(block.alt || '')} />
                            {block.caption && (
                                <figcaption className="block-caption">
                                    {sanitizeContent(decodeHtml(block.caption))}
                                </figcaption>
                            )}
                        </figure>
                    );
                case 'list':
                    return (
                        <ul
                            key={index}
                            className="block-list"
                            dangerouslySetInnerHTML={{ __html: sanitizeContent(block.items) }}
                        />
                    );
                case 'quote':
                    return (
                        <blockquote key={index} className="block-quote">
                            <p dangerouslySetInnerHTML={{ __html: sanitizeContent(block.content) }} />
                        </blockquote>
                    );
                case 'table':
                    return (
                        <div
                            key={index}
                            className="block-table"
                            dangerouslySetInnerHTML={{ __html: sanitizeContent(block.content) }}
                        />
                    );
                case 'html':
                    // Intercetta i post di Twitter e usa XEmbed
                    const twitterUrlMatch = block.content.match(/https:\/\/twitter\.com\/\w+\/status\/\d+/);
                    if (twitterUrlMatch) {
                        const twitterUrl = twitterUrlMatch[0];
                        return (
                            <div key={index} style={{display:'flex', justifyContent:'center'}}>
                                <XEmbed url={twitterUrl} style={{display:'contents', width:'100%'}}/>
                            </div>
                        );
                    }
                    // Se non è un URL Twitter, rendilo come HTML normale
                    return (
                        <div
                            key={index}
                            className="block-html"
                            dangerouslySetInnerHTML={{ __html: sanitizeContent(block.content) }}
                        />
                    );
                case 'video':
                    return (
                        <div key={index} className="block-video">
                            <video
                                src={block.src}
                                controls
                                width="100%"
                            >
                                Your browser does not support the video tag.
                            </video>
                        </div>
                    );
                case 'embed':
                    return (
                        <div key={index} className="block-embed" style={{ justifyContent:'center', display:'flex', alignItems: 'center', textAlign: 'center'}}>
                            {block.service === 'twitter' ? (
                                <XEmbed url={normalizedSrc} />
                            ) : block.service === 'instagram' ? (
                                <InstagramEmbed url={normalizedSrc} />
                            ) : block.service === 'facebook' ? (
                                <FacebookEmbed url={normalizedSrc} />
                            ) : block.service === 'youtube' ? (
                                <YouTubeEmbed url={normalizedSrc} width={'100%'} />
                            ) : (
                                <iframe
                                    src={normalizedSrc}
                                    title={block.service || 'Embed'}
                                    width="100%"
                                    height="600"
                                    frameBorder="0"
                                    allowFullScreen
                                ></iframe>
                            )}
                        </div>
                    );
                    case 'slideshow':
                        return (
                            <div key={index} className="block-slideshow">
                                <Slideshow slides={block.slides} />
                            </div>
                        );
                        case 'gallery':
                            return (
                                <>
                                    <style>
                                        {`
                                            .block-gallery {
                                                display: grid;
                                                grid-template-columns: 1fr;
                                                gap: 1rem;
                                            }
                        
                                            @media (min-width: 768px) {
                                                .block-gallery {
                                                    grid-template-columns: repeat(3, 1fr);
                                                }
                                            }
                        
                                            .gallery-item {
                                                width: 100%;
                                                margin: 0;
                                                cursor: pointer;
                                            }
                        
                                            .gallery-item img {
                                                width: 100%;
                                                height: auto;
                                                display: block;
                                            }
                                        `}
                                    </style>
                                    <div key={index} className="block-gallery">
                                        {block.images && block.images.map((img, imgIndex) => (
                                            <figure
                                                key={imgIndex}
                                                className="gallery-item"
                                                onClick={() => {
                                                    setLightboxImages(block.images);
                                                    setLightboxIndex(imgIndex);
                                                }}
                                            >
                                                <img src={img.src} alt={sanitizeContent(img.alt || '')} />
                                                {img.caption && (
                                                    <figcaption className="gallery-caption">
                                                        {sanitizeContent(decodeHtml(img.caption))}
                                                    </figcaption>
                                                )}
                                            </figure>
                                        ))}
                                    </div>
                                </>
                            );
                        
                default:
                    return (
                        <div key={index} className="block-unknown">
                            <p>Unsupported block type:</p>
                            <pre>{JSON.stringify(block, null, 2)}</pre>
                        </div>
                    );
            }
        });
    };

    return (
        <>
            {renderBlocks(blocks)}
            {lightboxIndex !== null && lightboxImages[lightboxIndex] && (
    <div
        className="lightbox-overlay"
        onClick={() => setLightboxIndex(null)}
        style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            overflow: 'hidden',
        }}
    >
        <div
            style={{
                position: 'relative',
                maxWidth: '100%',
                maxHeight: '100%',
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            }}
            onClick={(e) => e.stopPropagation()}
        >
            <img
                src={lightboxImages[lightboxIndex].src}
                alt={sanitizeContent(lightboxImages[lightboxIndex].alt || '')}
                style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                }}
            />
            {lightboxImages[lightboxIndex].caption && (
                <div
                    style={{
                        position: 'absolute',
                        bottom: '1rem',
                        left: '0',
                        right: '0',
                        textAlign: 'center',
                        color: '#fff',
                        fontSize: '1rem',
                        padding: '0 1rem',
                    }}
                >
                    {sanitizeContent(decodeHtml(lightboxImages[lightboxIndex].caption))}
                </div>
            )}
            {/* Pulsante chiudi visibile */}
            <button
                onClick={() => setLightboxIndex(null)}
                style={{
                    position: 'absolute',
                    top: '1rem',
                    right: '1rem',
                    fontSize: '2rem',
                    color: '#fff',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    zIndex: 10000,
                }}
            >
                ✕
            </button>
            {/* Navigazione */}
            {lightboxIndex > 0 && (
                <button onClick={() => setLightboxIndex(lightboxIndex - 1)} style={navButtonStyle('left')}>‹</button>
            )}
            {lightboxIndex < lightboxImages.length - 1 && (
                <button onClick={() => setLightboxIndex(lightboxIndex + 1)} style={navButtonStyle('right')}>›</button>
            )}
        </div>
    </div>
)}
        </>
)};

export default useRenderBlocks;